import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, Divider, Drawer, IconButton, ListItemButton, Stack, Typography } from '@mui/material';
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import {
  ILeftInnerNav,
  ILeftInnerNavPath,
  INavButton,
  analyticsArray,
  emissionsArray,
  navArray,
  organisationArray,
  partnerArray,
  reductionArray,
} from '../../shared/data/navigation.data';
import { ScreenLabelsContext } from '../../context/screen-labels/screen-labels';
import { IRoleType } from '../../shared/services/user-details/user-details.model';
import { UserDetailsContext } from '../../context/user-details/user-details';
import { Link, useLocation } from 'react-router-dom';
import { IconPicker } from '../../components/presentation-components/display-elements-components/icon-picker';
import { ReportPeriodContext } from '../../context/report-periods/report-periods';
import { userHasRequiredRole as hasRequiredRole } from '../../shared/utils/role-utils';

const SECTIONS_WITH_SUBS = new Set(['Organisation', 'Emissions', 'Reduction Plan', 'Analytics']);

function submenuForTopLevel(name: string): ILeftInnerNav[] | null {
  switch (name) {
    case 'Organisation':
      return organisationArray;
    case 'Emissions':
      return emissionsArray;
    case 'Reduction Plan':
      return reductionArray;
    case 'Analytics':
      return analyticsArray;
    default:
      return null;
  }
}

export const MobileNavbar: FunctionComponent<{ openMobileNavBar: boolean; toggleDrawer: () => void }> = ({
  openMobileNavBar,
  toggleDrawer,
}) => {
  const location = useLocation();
  const userDetailsContext = useContext(UserDetailsContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userDetails = userDetailsContext.userDetails;
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const selectedReportPeriod = useMemo(
    () => reportPeriodContext.allReportPeriods?.find((p) => p.id === reportPeriodContext.lastSelectedReportId),
    [reportPeriodContext.allReportPeriods, reportPeriodContext.lastSelectedReportId],
  );

  const reportingYearSubtitle = selectedReportPeriod
    ? `${selectedReportPeriod.calendarYear} Reporting Year`
    : undefined;

  function userHasRequiredRole(allowedRoles: IRoleType[]): boolean {
    return hasRequiredRole(userDetails, allowedRoles);
  }

  useEffect(() => {
    for (const nav of navArray) {
      if (!SECTIONS_WITH_SUBS.has(nav.name)) continue;
      const subs = submenuForTopLevel(nav.name);
      if (nav.isActive(location.pathname)) {
        setExpandedKey(nav.name);
        return;
      }
      if (subs?.some((s) => s.subcategory.some((sub) => sub.isActive(location.pathname)))) {
        setExpandedKey(nav.name);
        return;
      }
    }
    setExpandedKey(null);
  }, [location.pathname]);

  const organisationSections = useMemo(() => {
    // Orgs that customised the "product" screen label see their own term folded into the
    // split entries; the default ("Products") keeps the nav's static names. Mirrors
    // inner-left-navbar.
    const customProductsLabel = screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' });
    const hasCustomProductsLabel = !!customProductsLabel && customProductsLabel !== 'Products';
    const purchasedGoodsName = (name: string) =>
      hasCustomProductsLabel ? `Purchased & Capital ${customProductsLabel}` : name;
    const soldProductsName = (name: string) =>
      hasCustomProductsLabel ? `Sold ${customProductsLabel}` : name;
    return organisationArray.map((section) => ({
      ...section,
      category: screenLabelsContext.getLabel('organisation', { capitalize: 'all' }) || '',
      subcategory: section.subcategory
        .map((sub) =>
          sub.to === '/organisation/subsidiaries'
            ? { ...sub, name: screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' }) }
            : sub.to === '/organisation/locations'
              ? { ...sub, name: screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' }) }
              : sub.to === '/organisation/vehicles'
                ? { ...sub, name: screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' }) }
                : sub.to === '/organisation/projects'
                  ? { ...sub, name: screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' }) }
                  : sub.to === '/organisation/suppliers'
                    ? { ...sub, name: screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' }) }
                    : sub.to === '/organisation/products'
                      ? { ...sub, name: purchasedGoodsName(sub.name) }
                      : sub.to === '/organisation/sold-products'
                        ? { ...sub, name: soldProductsName(sub.name) }
                        : sub,
        )
        .filter((sub) => sub.to !== '/organisation/subsidiaries' || (userDetails?.organisation?.enableSubsidiaries ?? false))
        .filter((sub) => sub.to !== '/organisation/projects' || (userDetails?.organisation?.enableProjects ?? false))
        .filter((sub) => sub.to !== '/organisation/renewable-energy-certificates' || (userDetails?.organisation?.enableRecs ?? false)),
    }));
  }, [screenLabelsContext, userDetails?.organisation]);

  const resolveSubmenus = (navName: string): ILeftInnerNav[] | null => {
    if (navName === 'Organisation') return organisationSections;
    if (navName === 'Reduction Plan') {
      return reductionArray.map((section) => ({
        ...section,
        subcategory: section.subcategory.filter((sub) => (
          sub.to !== '/reduction-plan/flag' || (userDetails?.organisation?.isFlagEnabled ?? false)
        )),
      }));
    }
    return submenuForTopLevel(navName);
  };

  const navLocked = !userDetails?.organisation.subscriptionActive || userDetails?.organisation.name === null;

  const displayNameForNav = (object: INavButton) =>
    object.name === 'Organisation'
      ? screenLabelsContext.getLabel('organisation', { plural: false, capitalize: 'first' })
      : object.name;

  const renderSubLink = (setting: ILeftInnerNavPath, name: string) => {
    const isActive = setting.isActive(location.pathname);
    const destination = userDetails?.organisation.name == null ? '/settings/company-settings/overview' : setting.to;
    const isLocked = userDetails?.organisation.name == null && !isActive;
    return (
      <ListItemButton
        key={setting.to}
        component={Link}
        to={destination}
        disableRipple
        disabled={navLocked || isLocked}
        onClick={toggleDrawer}
        sx={{
          py: 1.25,
          px: 2,
          mb: 0.5,
          borderRadius: 0,
          justifyContent: 'flex-start',
          borderLeft: '3px solid',
          borderColor: isActive ? 'primary.main' : 'transparent',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: isLocked ? 'transparent' : 'action.hover',
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 600 : 400,
            color: 'text.primary',
            textAlign: 'left',
            width: '100%',
          }}
        >
          {name}
        </Typography>
      </ListItemButton>
    );
  };

  const renderSubmenu = (navItem: INavButton) => {
    const subs = resolveSubmenus(navItem.name);
    if (!subs) return null;
    return (
      <Box
        sx={{
          pl: 5.5,
          pr: 2,
        }}
      >
        <Stack sx={{ py: 1 }}>
          {subs.map((section) =>
            userHasRequiredRole(section.allowedRoles) ? (
              <Stack key={`${navItem.name}-${section.category}`}>
                {section.subcategory.filter((setting) => !setting.allowedRoles || userHasRequiredRole(setting.allowedRoles)).map((setting) => renderSubLink(setting, setting.name))}
              </Stack>
            ) : null,
          )}
        </Stack>
      </Box>
    );
  };

  const renderExpandableTopLevel = (object: INavButton, index: number) => {
    const isExpanded = expandedKey === object.name;
    const sectionActive = object.isActive(location.pathname);
    const iconActive = sectionActive || isExpanded;

    return (
      <Box key={index}>
        <ListItemButton
          disableRipple
          disabled={navLocked}
          onClick={() => {
            setExpandedKey((prev) => (prev === object.name ? null : object.name));
          }}
          sx={{
            py: 1.5,
            px: 2,
            mx: 0,
            borderRadius: 0,
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: isExpanded ? 'action.hover' : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: 28, justifyContent: 'center', flexShrink: 0 }}>
            <IconPicker icon={iconActive ? object.activeIcon : object.icon} color="text.primary" scale={0.95} />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', flex: 1, textAlign: 'left' }}>
            {displayNameForNav(object)}
          </Typography>
          {isExpanded ? (
            <ExpandLess sx={{ color: 'text.secondary', flexShrink: 0 }} />
          ) : (
            <ExpandMore sx={{ color: 'text.secondary', flexShrink: 0 }} />
          )}
        </ListItemButton>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {renderSubmenu(object)}
        </Collapse>
        <Divider sx={{ borderColor: 'divider' }} />
      </Box>
    );
  };

  const renderLeafTopLevel = (object: INavButton, index: number) => {
    const isActive = object.isActive(location.pathname);
    return (
      <Box key={index}>
        <ListItemButton
          component={Link}
          to={object.to}
          disableRipple
          disabled={navLocked}
          onClick={toggleDrawer}
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: 0,
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: 28, justifyContent: 'center', flexShrink: 0 }}>
            <IconPicker icon={isActive ? object.activeIcon : object.icon} color="text.primary" scale={0.95} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'text.primary', flex: 1, textAlign: 'left' }}
          >
            {displayNameForNav(object)}
          </Typography>
        </ListItemButton>
        <Divider sx={{ borderColor: 'divider' }} />
      </Box>
    );
  };

  const identityTitle =
    userDetails?.organisation?.name?.trim() ||
    (userDetails?.isPartner && !userDetails?.isLoggedInAsClient ? 'Partner' : '');

  return (
    <Drawer
      anchor="left"
      open={openMobileNavBar}
      variant="temporary"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          mt: 13,
          backgroundColor: 'common.white',
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            pt: 1.5,
            px: 2,
            pb: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left', pr: 0.5 }}>
            {(!userDetails?.isPartner || userDetails?.isLoggedInAsClient) && (
              <>
                {identityTitle ? (
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2, pt: 0.5 }}>
                    {identityTitle}
                  </Typography>
                ) : null}
                {reportingYearSubtitle ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: identityTitle ? 0.5 : 0 }}>
                    {reportingYearSubtitle}
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
          <IconButton
            onClick={toggleDrawer}
            sx={{ flexShrink: 0, p: 0.5, mt: -0.25, alignSelf: 'flex-start' }}
            aria-label="Close menu"
          >
            <Close sx={{ cursor: 'pointer', color: 'primary.main' }} />
          </IconButton>
        </Box>

        {!userDetails?.isPartner || userDetails?.isLoggedInAsClient ? (
          <>
            <Divider sx={{ borderColor: 'divider' }} />
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {navArray.map((object: INavButton, index: number) => {
                if (object.name === 'Product Carbon Footprint' && !userDetails?.organisation.enableProductCarbonFootprint) {
                  return null;
                }
                if (!userHasRequiredRole(object.allowedRoles)) return null;
                if (SECTIONS_WITH_SUBS.has(object.name)) {
                  return renderExpandableTopLevel(object, index);
                }
                return renderLeafTopLevel(object, index);
              })}
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {partnerArray.map((object: INavButton, index: number) => {
              const isActive = object.isActive(location.pathname);
              return (
                <Box key={index}>
                  <ListItemButton
                    component={Link}
                    to={object.to}
                    disableRipple
                    disabled={!userDetails?.organisation.subscriptionActive}
                    onClick={toggleDrawer}
                    sx={{
                      py: 1.5,
                      px: 2,
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 28, justifyContent: 'center', flexShrink: 0 }}>
                      <IconPicker icon={isActive ? object.activeIcon : object.icon} color="text.primary" scale={0.95} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, textAlign: 'left' }}>
                      {object.name}
                    </Typography>
                  </ListItemButton>
                  <Divider sx={{ borderColor: 'divider' }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};
